import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { Link } from '@tanstack/react-router'
import { useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { Share2 } from 'lucide-react'
import { motion, useReducedMotion } from 'motion/react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PACE_CLASS } from '@/components/goal-card'
import { StrengthChip } from '@/components/habit-strength'
import { StreakFlame } from '@/components/streak-icons'
import { useGrowIn } from '@/hooks/use-grow-in'
import { CATEGORY_COLORS, useCategoryFill } from '@/lib/category-color'
import { shareWeekCard } from '@/lib/share-week-card'
import { parseTaskFields } from '@/lib/tags'
import { goalSlug } from '@/lib/utils'

export type Stats = NonNullable<FunctionReturnType<typeof api.stats.getStats>>
export type StatsRange = Stats['range']
export const RANGES: StatsRange[] = ['7d', '30d', '90d', '1y']

const DAY = 86_400_000
const addDays = (key: string, n: number) =>
  new Date(Date.parse(`${key}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10)
// Jan 1 2024 is a Monday, so index 0 lines up with the backend's Monday-first weekdays.
const weekdayName = (lang: string, i: number, weekday: 'narrow' | 'long') =>
  new Date(Date.UTC(2024, 0, 1 + i)).toLocaleDateString(lang, { weekday, timeZone: 'UTC' })
const hourName = (lang: string, h: number) =>
  new Date(Date.UTC(2024, 0, 1, h % 24)).toLocaleTimeString(lang, {
    hour: 'numeric',
    timeZone: 'UTC',
  })
const partOfDay = (mid: number) =>
  mid >= 5 && mid < 12
    ? 'morning'
    : mid >= 12 && mid < 17
      ? 'afternoon'
      : mid >= 17 && mid < 22
        ? 'evening'
        : 'night'

const card = 'flex flex-col gap-2.5 rounded-2xl border border-border/60 bg-card/40 p-4'
const grow = 'motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-out'
const HEAT_LEVEL = ['bg-foreground/[0.06]', 'bg-primary/30', 'bg-primary/60', 'bg-primary']
const heatLevel = (n: number, max: number) => (n === 0 ? 0 : Math.min(3, Math.ceil((n / max) * 3)))

export function StatsView({
  stats,
  onRange,
  heading,
  top,
}: {
  stats: Stats
  onRange?: (r: StatsRange) => void
  // Shown under the title, above everything else (the productivity level).
  top?: React.ReactNode
  // Replaces the default "Stats / Your Progress" title (e.g. with view tabs).
  heading?: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {heading ?? (
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t('nav.stats')}
            </p>
            <h1 className="text-3xl font-black text-foreground">{t('stats.title')}</h1>
          </div>
        )}
        <div className="flex gap-0.5 rounded-full border border-border p-[3px]">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => onRange?.(r)}
              aria-pressed={stats.range === r}
              className={cn(
                'cursor-pointer rounded-full px-3 py-0.5 text-xs font-semibold transition-colors',
                stats.range === r
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t(`stats.range.${r}`)}
            </button>
          ))}
        </div>
      </div>
      {top}
      <Headline stats={stats} />
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,260px),1fr))] gap-3">
        <WeekCard stats={stats} />
        <RecordsCard stats={stats} />
      </div>
      <Heatmap stats={stats} />
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,260px),1fr))] gap-3">
        <GoalsByPace stats={stats} />
        <WhenCard stats={stats} />
      </div>
      <CategoryCard stats={stats} />
      {stats.habits.length > 0 && <HabitsCard stats={stats} />}
    </div>
  )
}

const pulse = 'animate-pulse rounded bg-muted'
const pair = 'grid grid-cols-[repeat(auto-fit,minmax(min(100%,260px),1fr))] gap-3'

// Mirrors StatsView: title + range pill, headline, week/records, heatmap,
// pace/when, categories.
export function StatsSkeleton({ heading }: { heading?: React.ReactNode }) {
  const block = (h: number) => (
    <div className={card} style={{ height: h }}>
      <div className={cn(pulse, 'h-3.5 w-24')} />
      <div className={cn(pulse, 'mt-1 flex-1 rounded-lg bg-muted/50')} />
    </div>
  )
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {heading ?? (
          <div>
            <div className={cn(pulse, 'mb-2 h-3 w-14')} />
            <div className={cn(pulse, 'h-9 w-48 rounded-lg')} />
          </div>
        )}
        <div className={cn(pulse, 'h-7 w-40 rounded-full bg-muted/60')} />
      </div>
      <section className={cn(card, 'gap-2')}>
        <div className={cn(pulse, 'h-8 w-3/4 rounded-lg')} />
        <div className={cn(pulse, 'h-3 w-32 bg-muted/60')} />
        <div className={cn(pulse, 'h-12 w-full rounded-lg bg-muted/40')} />
        <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-border/60 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="border-border/60 px-3 py-2 odd:border-r [&:nth-child(-n+2)]:border-b sm:border-r sm:last:border-r-0 sm:[&:nth-child(-n+2)]:border-b-0"
            >
              <div className={cn(pulse, 'h-5 w-12')} />
              <div className={cn(pulse, 'mt-1.5 h-2.5 w-16 bg-muted/60')} />
            </div>
          ))}
        </div>
      </section>
      <div className={pair}>
        {block(150)}
        {block(150)}
      </div>
      {block(180)}
      <div className={pair}>
        {block(170)}
        {block(170)}
      </div>
      {block(140)}
    </div>
  )
}

function Headline({ stats }: { stats: Stats }) {
  const { t } = useTranslation()
  const { totals } = stats
  const { deltaPct } = totals
  const series = stats.daily.map((d) => d.tasks + d.checkIns)
  const max = Math.max(1, ...series)
  const pts = series.map(
    (n, i) =>
      `${((i / Math.max(1, series.length - 1)) * 300).toFixed(1)} ${(44 - (n / max) * 40).toFixed(1)}`,
  )
  const line = `M${pts.join(' L')}`
  const clip = useId()
  const reduce = useReducedMotion()
  const cells = [
    { value: `${stats.activeDays}/${stats.rangeDays}`, label: t('stats.activeDays') },
    {
      value: (
        <>
          <StreakFlame /> {stats.streak}
        </>
      ),
      label: t('stats.streakBest', { best: stats.bestStreak }),
    },
    { value: stats.goalsActive, label: t('stats.goalsActive') },
    { value: stats.milestonesHit, label: t('stats.milestonesHit') },
  ]
  return (
    <section className={cn(card, 'gap-2')}>
      <div className="flex flex-col gap-0.5">
        <p className="text-3xl font-black leading-tight tracking-tight text-foreground">
          {t('stats.tasksCount', { count: totals.tasks })} ·{' '}
          {t('stats.checkInsCount', { count: totals.checkIns })}
        </p>
        <p className="text-xs text-muted-foreground">
          {deltaPct !== null && (
            <span
              className={cn(
                'mr-1 font-bold',
                deltaPct >= 0
                  ? 'text-lime-700 dark:text-lime-400'
                  : 'text-amber-700 dark:text-amber-400',
              )}
            >
              {deltaPct >= 0 ? '↑' : '↓'} {Math.abs(deltaPct)}%
            </span>
          )}
          {deltaPct === null
            ? t('stats.noPrevious')
            : stats.range === '1y'
              ? t('stats.vsPrevYear')
              : t('stats.vsPrevDays', { count: stats.rangeDays })}
        </p>
      </div>
      <svg viewBox="0 0 300 46" preserveAspectRatio="none" className="h-12 w-full" aria-hidden>
        <clipPath id={clip}>
          <motion.rect
            key={stats.range}
            height={46}
            initial={{ width: reduce ? 300 : 0 }}
            animate={{ width: 300 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          />
        </clipPath>
        <g clipPath={`url(#${clip})`}>
          <path d={`${line} L300 46 L0 46Z`} className="fill-primary/12" />
          <path
            d={line}
            fill="none"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            className="stroke-primary"
          />
        </g>
      </svg>
      <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-border/60 sm:grid-cols-4">
        {cells.map((c, i) => (
          <div
            key={i}
            className="min-w-0 border-border/60 px-3 py-2 odd:border-r [&:nth-child(-n+2)]:border-b sm:border-r sm:last:border-r-0 sm:[&:nth-child(-n+2)]:border-b-0"
          >
            <p className="text-lg font-extrabold tabular-nums text-foreground">{c.value}</p>
            <p className="truncate text-[11px] text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

function Heatmap({ stats }: { stats: Stats }) {
  const { t, i18n } = useTranslation()
  const [selected, setSelected] = useState<string | null>(null)
  const start = addDays(stats.today, -(stats.heatmap.length - 1))
  const lead = (new Date(`${start}T12:00:00Z`).getUTCDay() + 6) % 7
  const cells = [...Array<null>(lead).fill(null), ...stats.heatmap]
  const max = Math.max(1, ...stats.heatmap)
  const cols = Math.ceil(cells.length / 7)
  const months: { col: number; label: string }[] = []
  stats.heatmap.forEach((_, i) => {
    const key = addDays(start, i)
    const col = Math.floor((i + lead) / 7)
    if (key.endsWith('-01') && (months.length === 0 || col - months[months.length - 1].col >= 3))
      months.push({
        col,
        label: new Date(`${key}T12:00:00Z`).toLocaleDateString(i18n.language, {
          month: 'short',
          timeZone: 'UTC',
        }),
      })
  })
  const dateLabel = (i: number) =>
    new Date(`${addDays(start, i)}T12:00:00Z`).toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    })
  return (
    <section className={card}>
      <h2 className="flex items-center justify-between text-sm font-semibold text-foreground">
        {t('stats.lastYear')}
        <span className="text-[11px] font-medium text-muted-foreground">
          {t('stats.lastYearSub')}
        </span>
      </h2>
      <div>
        <div
          className="mb-1 grid h-3 text-[9.5px] leading-none text-muted-foreground/60"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {months.map((m) => (
            <span key={m.col} className="whitespace-nowrap" style={{ gridColumnStart: m.col + 1 }}>
              {m.label}
            </span>
          ))}
        </div>
        <div
          className="grid grid-flow-col grid-rows-7 gap-[2px] sm:gap-[3px]"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {cells.map((n, i) =>
            n === null ? (
              <span key={i} />
            ) : (
              <button
                key={i}
                type="button"
                title={`${dateLabel(i - lead)} · ${n}`}
                aria-pressed={selected === addDays(start, i - lead)}
                onClick={() => {
                  const key = addDays(start, i - lead)
                  setSelected((cur) => (cur === key ? null : key))
                }}
                className={cn(
                  'aspect-square cursor-pointer rounded-[2px] transition-transform hover:scale-125 sm:rounded-[3px]',
                  HEAT_LEVEL[heatLevel(n, max)],
                  i === cells.length - 1 && 'ring-[1.5px] ring-inset ring-foreground',
                  selected === addDays(start, i - lead) && 'ring-2 ring-foreground',
                )}
              />
            ),
          )}
        </div>
      </div>
      {selected && (
        <DayDetail
          date={selected}
          label={new Date(`${selected}T12:00:00Z`).toLocaleDateString(i18n.language, {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          })}
          onClose={() => setSelected(null)}
        />
      )}
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground/60">
        <span className="mr-auto">{!selected && t('stats.tapDay')}</span>
        {t('stats.less')}
        {HEAT_LEVEL.map((c) => (
          <span key={c} className={cn('h-2.5 w-2.5 rounded-[2px]', c)} />
        ))}
        {t('stats.more')}
      </div>
    </section>
  )
}

function GoalsByPace({ stats }: { stats: Stats }) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const grown = useGrowIn()
  return (
    <section className={card}>
      <h2 className="flex items-center justify-between text-sm font-semibold text-foreground">
        {t('stats.goalsTitle')}
        <span className="text-[11px] font-medium text-muted-foreground">{t('stats.byPace')}</span>
      </h2>
      {stats.goals.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('stats.noGoals')}</p>
      ) : (
        <div className="divide-y divide-border/60">
          {stats.goals.map((g) => {
            const color = fill(CATEGORY_COLORS[g.category.toLowerCase()] ?? '#6b7280')
            return (
              <Link
                key={g.goalId}
                to="/goals/$goalId"
                params={{ goalId: goalSlug(g.title, g.goalId) }}
                className="flex flex-col gap-1 py-2 first:pt-0 last:pb-0"
              >
                <div className="flex min-w-0 items-center gap-2 text-[13px] font-semibold text-foreground">
                  <span
                    className="h-[7px] w-[7px] shrink-0 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="min-w-0 flex-1 truncate">{g.title}</span>
                  {g.pace && (
                    <span
                      className={cn(
                        'whitespace-nowrap rounded-full px-2 py-px text-[10px] font-bold',
                        PACE_CLASS[g.pace],
                      )}
                    >
                      {g.pace === 'quiet'
                        ? t('goals.paceQuiet', { count: g.quietDays })
                        : t(g.pace === 'onPace' ? 'goals.paceOnPace' : 'goals.paceBehind')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 pl-[15px] text-[11px] text-muted-foreground">
                  {g.milestone && (
                    <span className="min-w-0 truncate">
                      {t('stats.milestoneLine', {
                        index: g.milestone.index,
                        total: g.milestone.total,
                        title: g.milestone.title,
                      })}
                    </span>
                  )}
                  <span className="ml-auto h-[3px] w-full max-w-[120px] shrink-0 overflow-hidden rounded-full bg-border sm:w-[120px]">
                    <span
                      className={cn('block h-full rounded-full', grow)}
                      style={{ width: `${grown ? g.progress : 0}%`, backgroundColor: color }}
                    />
                  </span>
                  <span className="w-8 shrink-0 text-right font-mono text-[10px] tabular-nums">
                    {g.progress}%
                  </span>
                </div>
              </Link>
            )
          })}
        </div>
      )}
      <Link to="/goals" className="text-xs font-semibold text-primary-text hover:underline">
        {t('stats.allGoals', { count: stats.goalsActive })}
      </Link>
    </section>
  )
}

function WhenCard({ stats }: { stats: Stats }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const max = Math.max(1, ...stats.byWeekday)
  const { peak } = stats
  const grown = useGrowIn()
  return (
    <section className={card}>
      <h2 className="text-sm font-semibold text-foreground">{t('stats.whenTitle')}</h2>
      <div className="grid h-20 grid-cols-7 items-end gap-1">
        {stats.byWeekday.map((n, i) => (
          <div
            key={i}
            className="flex h-full flex-col items-center justify-end gap-1 text-[9.5px] text-muted-foreground/60"
          >
            <span
              className={cn(
                'block w-full max-w-[26px] rounded-t-[4px] rounded-b-[2px]',
                grow,
                peak?.weekday === i ? 'bg-primary' : 'bg-foreground/[0.08]',
              )}
              style={{
                height: `${Math.max(4, grown ? (n / max) * 100 : 0)}%`,
                transitionDelay: `${i * 40}ms`,
              }}
              title={String(n)}
            />
            {weekdayName(lang, i, 'narrow')}
          </div>
        ))}
      </div>
      <p className="rounded-xl border border-primary/45 bg-primary/12 px-2.5 py-1.5 text-xs text-foreground">
        {peak
          ? `${t(`stats.peak.${partOfDay(peak.hourStart + 1)}`, {
              day: weekdayName(lang, peak.weekday, 'long'),
              hours: `${hourName(lang, peak.hourStart)}–${hourName(lang, peak.hourEnd)}`,
            })}${
              peak.quietWeekday !== peak.weekday
                ? ` ${t('stats.quietDay', { day: weekdayName(lang, peak.quietWeekday, 'long') })}`
                : ''
            }`
          : t('stats.peakNone')}
      </p>
    </section>
  )
}

function HabitsCard({ stats }: { stats: Stats }) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  return (
    <section className={card}>
      <h2 className="flex items-center justify-between text-sm font-semibold text-foreground">
        {t('stats.habits')}
        <span className="text-[11px] font-medium text-muted-foreground">
          {t('stats.mostAtRisk')}
        </span>
      </h2>
      <div className="divide-y divide-border/60">
        {stats.habits.map((h) => {
          const color = CATEGORY_COLORS[h.category.toLowerCase()]
          return (
            <div
              key={h.id}
              className="flex flex-wrap items-center gap-x-2.5 gap-y-1 py-1.5 text-xs first:pt-0 last:pb-0"
            >
              <span className="w-[52px] shrink-0 text-[9.5px] font-extrabold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                {h.atRisk ? t('stats.atRisk') : ''}
              </span>
              <span className="flex min-w-0 flex-1 basis-32 flex-col">
                <span className="truncate text-foreground">{h.title}</span>
                {h.atRisk && h.strength > h.strengthIfMissed && (
                  <span className="truncate font-mono text-[10px] text-muted-foreground">
                    {t('strength.dent', { from: h.strength, to: h.strengthIfMissed })}
                  </span>
                )}
              </span>
              <span className="flex gap-[2px]">
                {h.last30.map((done, i) => (
                  <span
                    key={i}
                    className={cn(
                      'h-[7px] w-[7px] rounded-[2px]',
                      done ? (color ? '' : 'bg-primary') : 'bg-foreground/[0.06]',
                    )}
                    style={done && color ? { backgroundColor: fill(color) } : undefined}
                  />
                ))}
              </span>
              <span className="w-[112px] text-right font-mono text-[10.5px] text-muted-foreground">
                <StreakFlame />
                {h.streak} · {h.pct}% · <StrengthChip value={h.strength} />
              </span>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function Delta({ cur, prev }: { cur: number; prev: number }) {
  const diff = cur - prev
  if (diff === 0) return <span className="text-muted-foreground/60">=</span>
  return (
    <span
      className={cn(
        'font-bold',
        diff > 0 ? 'text-lime-700 dark:text-lime-400' : 'text-amber-700 dark:text-amber-400',
      )}
    >
      {diff > 0 ? '↑' : '↓'}
      {Math.abs(diff)}
    </span>
  )
}

function WeekCard({ stats }: { stats: Stats }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const [sharing, setSharing] = useState(false)
  const { week } = stats
  const max = Math.max(1, ...week.days)
  const grown = useGrowIn()
  const rows = [
    { label: t('stats.tasks'), cur: week.cur.tasks, prev: week.prev.tasks },
    { label: t('stats.checkIns'), cur: week.cur.checkIns, prev: week.prev.checkIns },
    { label: t('stats.activeDaysLabel'), cur: week.cur.activeDays, prev: week.prev.activeDays },
  ]
  const share = async () => {
    setSharing(true)
    try {
      await shareWeekCard({
        weekLabel: t('stats.weekOf', {
          date: new Date(`${week.start}T12:00:00Z`).toLocaleDateString(lang, {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          }),
        }),
        tasks: t('stats.tasksCount', { count: week.cur.tasks }),
        checkIns: t('stats.checkInsCount', { count: week.cur.checkIns }),
        tasksN: week.cur.tasks,
        checkInsN: week.cur.checkIns,
        footer: [
          t('stats.activeDaysCount', { count: week.cur.activeDays }),
          stats.streak > 0 ? t('stats.streakCount', { count: stats.streak }) : null,
        ]
          .filter(Boolean)
          .join('  ·  '),
        days: week.days,
        elapsed: week.elapsed,
        dayLetters: Array.from({ length: 7 }, (_, i) => weekdayName(lang, i, 'narrow')),
        shareText: t('stats.shareText'),
      })
    } finally {
      setSharing(false)
    }
  }
  return (
    <section className={card}>
      <h2 className="flex items-center justify-between text-sm font-semibold text-foreground">
        {t('stats.weekTitle')}
        <button
          type="button"
          onClick={share}
          disabled={sharing}
          className="flex cursor-pointer items-center gap-1.5 rounded-full bg-primary/12 px-2.5 py-1 text-[11px] font-bold text-primary-text transition-colors hover:bg-primary/20 disabled:opacity-50"
        >
          <Share2 className="size-3" strokeWidth={2.5} />
          {t('stats.shareWeek')}
        </button>
      </h2>
      <div className="grid h-16 grid-cols-7 items-end gap-1">
        {week.days.map((n, i) => (
          <div
            key={i}
            className="flex h-full flex-col items-center justify-end gap-1 text-[9.5px] text-muted-foreground/60"
          >
            <span
              className={cn(
                'block w-full max-w-[26px] rounded-t-[4px] rounded-b-[2px]',
                grow,
                i >= week.elapsed
                  ? 'border border-dashed border-border bg-transparent'
                  : n > 0
                    ? 'bg-primary'
                    : 'bg-foreground/[0.08]',
              )}
              style={{
                height: i >= week.elapsed ? '18%' : `${Math.max(6, grown ? (n / max) * 100 : 0)}%`,
                transitionDelay: `${i * 40}ms`,
              }}
              title={String(n)}
            />
            {weekdayName(lang, i, 'narrow')}
          </div>
        ))}
      </div>
      <div className="divide-y divide-border/60 text-xs">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-2 py-1.5 first:pt-0 last:pb-0">
            <span className="flex-1 text-muted-foreground">{r.label}</span>
            <span className="font-extrabold tabular-nums text-foreground">{r.cur}</span>
            <span className="w-9 text-right font-mono text-[10.5px] tabular-nums">
              <Delta cur={r.cur} prev={r.prev} />
            </span>
          </div>
        ))}
      </div>
      <p className="text-[10.5px] text-muted-foreground/70">{t('stats.vsLastWeek')}</p>
    </section>
  )
}

function RecordsCard({ stats }: { stats: Stats }) {
  const { t, i18n } = useTranslation()
  const { records } = stats
  const tiles = [
    {
      label: t('stats.bestStreak'),
      value: (
        <>
          <StreakFlame /> {stats.bestStreak}
        </>
      ),
      sub: t('stats.daysCount', { count: stats.bestStreak }),
    },
    {
      label: t('stats.bestDay'),
      value: records.bestDay ? String(records.bestDay.count) : '—',
      sub: records.bestDay
        ? new Date(`${records.bestDay.date}T12:00:00Z`).toLocaleDateString(i18n.language, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
          })
        : t('stats.noneYet'),
    },
    {
      label: t('stats.doneThisYear'),
      value: String(records.yearTasks + records.yearCheckIns),
      sub: `${t('stats.tasksCount', { count: records.yearTasks })} · ${t('stats.checkInsCount', { count: records.yearCheckIns })}`,
    },
    {
      label: t('stats.longestHabitRun'),
      value: records.bestHabitRun ? String(records.bestHabitRun.days) : '—',
      sub: records.bestHabitRun
        ? parseTaskFields(records.bestHabitRun.title).title
        : t('stats.noneYet'),
    },
  ]
  return (
    <section className={card}>
      <h2 className="flex items-center justify-between text-sm font-semibold text-foreground">
        {t('stats.recordsTitle')}
        <span className="text-[11px] font-medium text-muted-foreground">{t('stats.pastYear')}</span>
      </h2>
      <div className="grid grid-cols-2 gap-2">
        {tiles.map((tile) => (
          <div key={tile.label} className="min-w-0 rounded-xl bg-foreground/[0.04] px-3 py-2.5">
            <p className="truncate text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground/70">
              {tile.label}
            </p>
            <p className="text-xl font-black tabular-nums text-foreground">{tile.value}</p>
            <p className="truncate text-[11px] text-muted-foreground">{tile.sub}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

const KNOWN_CATEGORIES = new Set([...Object.keys(CATEGORY_COLORS), 'inbox'])

function CategoryCard({ stats }: { stats: Stats }) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const merged = new Map<string, number>()
  for (const c of stats.categories) {
    const key = KNOWN_CATEGORIES.has(c.category) ? c.category : 'other'
    merged.set(key, (merged.get(key) ?? 0) + c.count)
  }
  const rows = [...merged].sort((a, b) => b[1] - a[1])
  const total = rows.reduce((s, [, n]) => s + n, 0)
  const colorOf = (c: string) =>
    CATEGORY_COLORS[c] ? fill(CATEGORY_COLORS[c]) : 'var(--muted-foreground)'
  const labelOf = (c: string) => (c === 'other' ? t('stats.otherCategory') : t(`goals.cat.${c}`))
  const grown = useGrowIn()
  return (
    <section className={card}>
      <h2 className="flex items-center justify-between text-sm font-semibold text-foreground">
        {t('stats.categoriesTitle')}
        <span className="text-[11px] font-medium text-muted-foreground">
          {t(`stats.rangeLabel.${stats.range}`)}
        </span>
      </h2>
      {total === 0 ? (
        <p className="text-xs text-muted-foreground">{t('stats.categoriesEmpty')}</p>
      ) : (
        <>
          <div className="flex h-2.5 gap-[2px] overflow-hidden rounded-full">
            {rows.map(([c, n]) => (
              <span
                key={c}
                className={grow}
                style={{
                  width: `${grown ? (n / total) * 100 : 0}%`,
                  backgroundColor: colorOf(c),
                }}
              />
            ))}
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-x-4 gap-y-1.5">
            {rows.map(([c, n]) => (
              <div key={c} className="flex items-center gap-2 text-xs">
                <span
                  className="size-2 shrink-0 rounded-[3px]"
                  style={{ backgroundColor: colorOf(c) }}
                />
                <span className="min-w-0 flex-1 truncate text-foreground">{labelOf(c)}</span>
                <span className="font-semibold tabular-nums text-foreground">{n}</span>
                <span className="w-8 text-right font-mono text-[10.5px] tabular-nums text-muted-foreground">
                  {Math.round((n / total) * 100)}%
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}

function DayDetail({ date, label, onClose }: { date: string; label: string; onClose: () => void }) {
  const { t } = useTranslation()
  const day = useQuery(api.stats.getDay, { date })
  const groups = day
    ? [
        { title: t('stats.dayTasks'), items: day.tasks },
        { title: t('stats.dayCheckIns'), items: day.checkIns },
        { title: t('stats.dayMilestones'), items: day.milestones },
      ].filter((g) => g.items.length > 0)
    : []
  return (
    <div className="flex flex-col gap-2 rounded-xl bg-foreground/[0.04] px-3 py-2.5">
      <div className="flex items-center justify-between text-xs font-semibold text-foreground">
        {label}
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer text-[11px] font-medium text-muted-foreground hover:text-foreground"
        >
          {t('common.close')}
        </button>
      </div>
      {day === undefined ? (
        <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
      ) : groups.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('stats.dayEmpty')}</p>
      ) : (
        groups.map((g) => (
          <div key={g.title} className="flex flex-col gap-1">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
              {g.title} · {g.items.length}
            </p>
            <ul className="flex flex-col gap-0.5 text-xs text-foreground">
              {g.items.map((title, i) => (
                <li key={i} className="flex gap-1.5">
                  <span className="text-primary-text">✓</span>
                  <span className="min-w-0">{parseTaskFields(title).title}</span>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  )
}

// Fake data for the blurred preview behind the Pro gate.
export function sampleStats(today: string): Stats {
  const wave = (i: number) => Math.max(0, Math.round(3 + 3 * Math.sin(i / 3) + ((i * 7) % 5) - 2))
  const daily = Array.from({ length: 30 }, (_, i) => ({
    date: addDays(today, i - 29),
    tasks: wave(i),
    checkIns: wave(i + 4),
  }))
  return {
    range: '30d',
    rangeDays: 30,
    today,
    daily,
    totals: { tasks: 42, checkIns: 63, prevTasks: 38, prevCheckIns: 51, deltaPct: 18 },
    activeDays: 22,
    streak: 9,
    bestStreak: 14,
    goalsActive: 7,
    milestonesHit: 2,
    heatmap: Array.from({ length: 365 }, (_, i) => ((i * 37) % 11 < 4 ? 0 : (i * 13) % 9)),
    categories: [
      { category: 'mind', count: 48 },
      { category: 'career', count: 31 },
      { category: 'health', count: 26 },
    ],
    records: {
      bestDay: { date: addDays(today, -40), count: 14 },
      yearTasks: 412,
      yearCheckIns: 655,
      bestHabitRun: { title: 'Drink water on waking', days: 21 },
    },
    week: {
      start: addDays(today, -3),
      elapsed: 4,
      days: [6, 9, 4, 7, 0, 0, 0],
      cur: { tasks: 11, checkIns: 15, activeDays: 4 },
      prev: { tasks: 9, checkIns: 16, activeDays: 3 },
    },
    byWeekday: [8, 19, 12, 11, 7, 2, 6],
    byHour: Array<number>(24).fill(1),
    peak: { weekday: 1, hourStart: 7, hourEnd: 10, quietWeekday: 5 },
    goals: (
      [
        ['Present a composition on stage', 'creative', 'onPace', 46, 2, 'Record demos'],
        ['Launch my first startup', 'career', 'behind', 10, 1, 'Build foundation'],
        ['Run a half marathon', 'health', 'quiet', 5, 1, 'Base mileage'],
      ] as const
    ).map(([title, category, pace, progress, index, ms], i) => ({
      goalId: `sample${i}` as Stats['goals'][number]['goalId'],
      title,
      category,
      pace,
      quietDays: 43,
      progress,
      milestone: { index, total: 4, title: ms },
    })),
    habits: (
      [
        ['Meditate 10 minutes', 0, 40, 38, true],
        ["Three things I'm grateful for", 3, 70, 72, false],
        ['Drink water on waking', 9, 93, 94, false],
      ] as const
    ).map(([title, streak, pct, strength, atRisk], i) => ({
      id: `habit${i}` as Stats['habits'][number]['id'],
      title,
      category: 'mind',
      last30: Array.from({ length: 30 }, (_, d) => (d * 17 + i * 5) % 100 < pct),
      streak,
      strength,
      strengthIfMissed: Math.round(strength * 0.93),
      pct,
      atRisk,
    })),
  }
}
